import { Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import CsvDataTable from "./CsvDataTable";

interface CsvDataViewerProps {
  open: boolean;
  onClose: () => void;
  /** dataset.original_file_key — the raw CSV S3 key the endpoint is keyed by. */
  fileKey: string | null | undefined;
  title?: string;
  /** Ordered column names from stac_properties.column_schema (optional hint). */
  columns?: string[];
}

/**
 * Read-only, paginated table view of a tabular dataset's CSV rows, shown in a dialog.
 * The table itself lives in {@link CsvDataTable} so it can be reused elsewhere (e.g. as
 * a MediaViewer renderer). No editing — the edit round-trip (download → Excel →
 * re-upload as a new version) is a separate, backend-dependent phase.
 */
export default function CsvDataViewer({ open, onClose, fileKey, title, columns }: CsvDataViewerProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack direction='row' alignItems='center' spacing={1}>
          <Typography variant='h6' sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {title || "Table data"}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }} size='small'>
          <Close fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* keepMounted defaults to false, so the table unmounts on close and page state
            resets automatically the next time the dialog opens. */}
        <CsvDataTable fileKey={fileKey} columns={columns} enabled={open} maxHeight='60vh' />
      </DialogContent>
    </Dialog>
  );
}
